import { useState, useEffect } from 'react';
import api from '../../utils/api';

export default function QuizManager({ courseId }) {
  const [activeTab, setActiveTab] = useState('section');
  const [sectionQuizzes, setSectionQuizzes] = useState([]);
  const [finalExam, setFinalExam] = useState(null);
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddQuestion, setShowAddQuestion] = useState(false);
  const [questionForm, setQuestionForm] = useState({
    questionText: '',
    explanation: '',
    options: [
      { text: '', isCorrect: false },
      { text: '', isCorrect: false },
      { text: '', isCorrect: false },
      { text: '', isCorrect: false },
    ]
  });
  const [newQuizTitle, setNewQuizTitle] = useState('');
  const [newQuizSectionId, setNewQuizSectionId] = useState('');
  const [sections, setSections] = useState([]);
  const [message, setMessage] = useState('');

  const loadSectionQuizzes = async () => {
    const res = await api.get(`/quizzes/admin/course/${courseId}/section-quizzes`);
    setSectionQuizzes(res.data.quizzes);
  };

  const loadFinalExam = async () => {
    const res = await api.get(`/quizzes/admin/course/${courseId}/final-exam`);
    setFinalExam(res.data.exam);
  };

  useEffect(() => {
    loadSectionQuizzes();
    loadFinalExam();
    api.get(`/admin/courses/${courseId}/sections`).then(r => setSections(r.data.sections)).catch(() => {});
  }, [courseId]);

  const loadQuestions = async (quizId, type) => {
    setLoading(true);
    try {
      const endpoint = type === 'section'
        ? `/quizzes/section/${quizId}/questions`
        : `/quizzes/final/${quizId}/questions`;
      const res = await api.get(endpoint);
      setQuestions(type === 'section' ? res.data.questions : res.data.questions);
      setSelectedQuiz({ id: quizId, type });
    } finally { setLoading(false); }
  };

  const createSectionQuiz = async () => {
    if (!newQuizTitle.trim()) return;
    if (!newQuizSectionId) { alert('Please select a section for this quiz.'); return; }
    try {
      await api.post('/quizzes/admin/section', { courseId, sectionId: newQuizSectionId, title: newQuizTitle, passingScore: 80 });
      setNewQuizTitle('');
      setNewQuizSectionId('');
      loadSectionQuizzes();
      setMessage('Section quiz created!');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) { alert(err.response?.data?.error || 'Failed'); }
  };

  const createFinalExam = async () => {
    try {
      await api.post('/quizzes/admin/final', { courseId, title: 'Final Exam', passingScore: 75 });
      loadFinalExam();
      setMessage('Final exam created!');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) { alert(err.response?.data?.error || 'Already exists or failed'); }
  };

  const addQuestion = async () => {
    if (!selectedQuiz) return;
    if (!questionForm.questionText.trim()) { alert('Please enter a question'); return; }
    if (questionForm.options.some(o => !o.text.trim())) { alert('Please fill in all answer options'); return; }
    if (!questionForm.options.some(o => o.isCorrect)) { alert('Please mark one answer as correct'); return; }

    try {
      const endpoint = selectedQuiz.type === 'section'
        ? `/quizzes/admin/section/${selectedQuiz.id}/questions`
        : `/quizzes/admin/final/${selectedQuiz.id}/questions`;

      await api.post(endpoint, {
        questionText: questionForm.questionText,
        explanation: questionForm.explanation,
        options: questionForm.options,
        sortOrder: questions.length,
      });

      setQuestionForm({ questionText: '', explanation: '', options: [{ text: '', isCorrect: false }, { text: '', isCorrect: false }, { text: '', isCorrect: false }, { text: '', isCorrect: false }] });
      setShowAddQuestion(false);
      loadQuestions(selectedQuiz.id, selectedQuiz.type);
      setMessage('Question added!');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) { alert(err.response?.data?.error || 'Failed to add question'); }
  };

  const deleteQuestion = async (questionId) => {
    if (!window.confirm('Delete this question?')) return;
    const endpoint = selectedQuiz.type === 'section'
      ? `/quizzes/admin/section/questions/${questionId}`
      : `/quizzes/admin/final/questions/${questionId}`;
    await api.delete(endpoint);
    loadQuestions(selectedQuiz.id, selectedQuiz.type);
  };

  const setCorrectOption = (index) => {
    setQuestionForm(f => ({
      ...f,
      options: f.options.map((o, i) => ({ ...o, isCorrect: i === index }))
    }));
  };

  return (
    <div style={styles.root}>
      <h3 style={styles.title}>📝 Quiz & Exam Manager</h3>

      {message && <div className="alert alert-success" style={{ marginBottom: 16 }}>{message}</div>}

      {/* Tabs */}
      <div style={styles.tabs}>
        <button style={{ ...styles.tab, ...(activeTab === 'section' ? styles.tabActive : {}) }} onClick={() => { setActiveTab('section'); setSelectedQuiz(null); setQuestions([]); }}>
          Section Quizzes ({sectionQuizzes.length})
        </button>
        <button style={{ ...styles.tab, ...(activeTab === 'final' ? styles.tabActive : {}) }} onClick={() => { setActiveTab('final'); setSelectedQuiz(null); setQuestions([]); }}>
          Final Exam{finalExam ? ` (${finalExam.question_count} questions, ${finalExam.passing_score || 75}% to pass)` : ''}
        </button>
      </div>

      {/* Section Quizzes Tab */}
      {activeTab === 'section' && (
        <div>
          {/* Create new section quiz */}
          <div style={styles.createBox}>
            <h4 style={styles.boxTitle}>Create Section Quiz</h4>
            <p style={styles.boxDesc}>Each section quiz has 5 questions. Students must pass with 80% to continue to the next section.</p>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <select className="input" value={newQuizSectionId} onChange={e => setNewQuizSectionId(e.target.value)} style={{ width: 200 }}>
                <option value="">Select section...</option>
                {sections.map(s => <option key={s.id} value={s.id}>{s.title}</option>)}
              </select>
              <input className="input" placeholder="Quiz title e.g. Agency Law Quiz" value={newQuizTitle} onChange={e => setNewQuizTitle(e.target.value)} style={{ flex: 1 }} />
              <button className="btn btn-primary" onClick={createSectionQuiz}>Create Quiz</button>
            </div>
          </div>

          {/* List of section quizzes */}
          {sectionQuizzes.length === 0 ? (
            <div style={styles.empty}>No section quizzes yet. Create one above.</div>
          ) : (
            <div style={styles.quizList}>
              {sectionQuizzes.map(quiz => (
                <div key={quiz.id} style={{ ...styles.quizItem, ...(selectedQuiz?.id === quiz.id ? styles.quizItemActive : {}) }}>
                  <div style={styles.quizInfo}>
                    <div style={styles.quizName}>{quiz.title}</div>
                    <div style={styles.quizMeta}>{quiz.question_count} questions • 80% to pass</div>
                  </div>
                  <button className="btn btn-primary btn-sm" onClick={() => loadQuestions(quiz.id, 'section')}>
                    Manage Questions
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Final Exam Tab */}
      {activeTab === 'final' && (
        <div>
          {!finalExam ? (
            <div style={styles.createBox}>
              <h4 style={styles.boxTitle}>Create Final Exam</h4>
              <p style={styles.boxDesc}>Students must pass the final exam to complete the course and receive their certificate. Question count + passing score are configured on the course itself.</p>
              <button className="btn btn-primary" onClick={createFinalExam}>Create Final Exam</button>
            </div>
          ) : (
            <div style={styles.quizList}>
              <div style={{ ...styles.quizItem, ...(selectedQuiz?.id === finalExam.id ? styles.quizItemActive : {}) }}>
                <div style={styles.quizInfo}>
                  <div style={styles.quizName}>{finalExam.title}</div>
                  <div style={styles.quizMeta}>
                    {finalExam.question_count} questions in pool
                    {finalExam.final_exam_draw_count ? ` • draws ${finalExam.final_exam_draw_count} per attempt` : ''}
                    {' • '}{finalExam.passing_score || 75}% to pass
                  </div>
                </div>
                <button className="btn btn-primary btn-sm" onClick={() => loadQuestions(finalExam.id, 'final')}>
                  Manage Questions
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Question Manager */}
      {selectedQuiz && (
        <div style={styles.questionManager}>
          <div style={styles.questionManagerHeader}>
            <h4 style={styles.boxTitle}>
              Questions ({questions.length} {selectedQuiz.type === 'final' ? '/ 25' : '/ 5'})
            </h4>
            <button className="btn btn-primary btn-sm" onClick={() => setShowAddQuestion(!showAddQuestion)}>
              {showAddQuestion ? 'Cancel' : '+ Add Question'}
            </button>
          </div>

          {/* Add question form */}
          {showAddQuestion && (
            <div style={styles.addQuestionForm}>
              <div className="form-group">
                <label className="form-label">Question *</label>
                <textarea className="input" rows={3} value={questionForm.questionText} onChange={e => setQuestionForm(f => ({ ...f, questionText: e.target.value }))} placeholder="Enter your question here..." style={{ resize: 'vertical' }} />
              </div>

              <div className="form-group">
                <label className="form-label">Answer Options (select the correct one)</label>
                {questionForm.options.map((opt, i) => (
                  <div key={i} style={styles.optionRow}>
                    <input
                      type="radio"
                      name="correctAnswer"
                      checked={opt.isCorrect}
                      onChange={() => setCorrectOption(i)}
                      style={{ accentColor: 'var(--forest-500)', flexShrink: 0, marginTop: 12 }}
                    />
                    <input
                      className="input"
                      placeholder={`Option ${String.fromCharCode(65 + i)}`}
                      value={opt.text}
                      onChange={e => setQuestionForm(f => ({ ...f, options: f.options.map((o, idx) => idx === i ? { ...o, text: e.target.value } : o) }))}
                      style={{ flex: 1, ...(opt.isCorrect ? { borderColor: 'var(--forest-500)', background: 'var(--forest-50)' } : {}) }}
                    />
                    {opt.isCorrect && <span style={{ color: 'var(--forest-600)', fontWeight: 700, fontSize: 12, flexShrink: 0 }}>✓ Correct</span>}
                  </div>
                ))}
              </div>

              <div className="form-group">
                <label className="form-label">Explanation (optional — shown after answering)</label>
                <input className="input" placeholder="Why is this the correct answer?" value={questionForm.explanation} onChange={e => setQuestionForm(f => ({ ...f, explanation: e.target.value }))} />
              </div>

              <button className="btn btn-primary" onClick={addQuestion}>Save Question</button>
            </div>
          )}

          {/* Questions list */}
          {loading ? <div className="loading-wrapper"><div className="spinner" /></div> : (
            <div style={styles.questionsList}>
              {questions.length === 0 ? (
                <div style={styles.empty}>No questions yet. Add your first question above.</div>
              ) : questions.map((q, i) => (
                <div key={q.id} style={styles.questionCard}>
                  <div style={styles.questionHeader}>
                    <span style={styles.questionNum}>Q{i + 1}</span>
                    <p style={styles.questionText}>{q.question_text}</p>
                    <button style={styles.deleteBtn} onClick={() => deleteQuestion(q.id)}>🗑️</button>
                  </div>
                  <div style={styles.optionsList}>
                    {q.options?.map(opt => (
                      <div key={opt.id} style={{ ...styles.optionItem, ...(opt.is_correct ? styles.optionCorrect : {}) }}>
                        {opt.is_correct ? '✓' : '○'} {opt.text}
                      </div>
                    ))}
                  </div>
                  {q.explanation && <p style={styles.explanation}>💡 {q.explanation}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const styles = {
  root: { marginTop: 24 },
  title: { fontSize: 18, fontWeight: 700, marginBottom: 16, color: 'var(--slate-900)' },
  tabs: { display: 'flex', gap: 8, marginBottom: 20, borderBottom: '2px solid var(--color-border)', paddingBottom: 0 },
  tab: { padding: '10px 20px', background: 'none', border: 'none', fontSize: 14, fontWeight: 600, color: 'var(--slate-500)', cursor: 'pointer', borderBottom: '2px solid transparent', marginBottom: -2 },
  tabActive: { color: 'var(--forest-600)', borderBottomColor: 'var(--forest-600)' },
  createBox: { background: 'var(--tan-50)', border: '1.5px solid #b39e89', borderRadius: 12, padding: '20px 24px', marginBottom: 20 },
  boxTitle: { fontSize: 15, fontWeight: 700, color: 'var(--forest-700)', marginBottom: 6 },
  boxDesc: { fontSize: 13, color: 'var(--color-text-muted)', marginBottom: 16, lineHeight: 1.5 },
  empty: { padding: '32px', textAlign: 'center', color: 'var(--color-text-muted)', fontSize: 14 },
  quizList: { display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 },
  quizItem: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', background: 'white', border: '1.5px solid var(--color-border)', borderRadius: 10, gap: 16 },
  quizItemActive: { borderColor: 'var(--forest-500)', background: 'var(--forest-50)' },
  quizInfo: {},
  quizName: { fontSize: 14, fontWeight: 700, color: 'var(--slate-800)' },
  quizMeta: { fontSize: 12, color: 'var(--color-text-muted)', marginTop: 2 },
  questionManager: { background: 'white', border: '1.5px solid #b39e89', borderRadius: 12, padding: '20px 24px', marginTop: 20 },
  questionManagerHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 },
  addQuestionForm: { background: 'var(--tan-50)', border: '1px solid var(--tan-200)', borderRadius: 10, padding: '20px', marginBottom: 20, display: 'flex', flexDirection: 'column', gap: 14 },
  optionRow: { display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 8 },
  questionsList: { display: 'flex', flexDirection: 'column', gap: 12 },
  questionCard: { border: '1px solid var(--color-border)', borderRadius: 10, padding: '16px', background: 'var(--slate-50)' },
  questionHeader: { display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 },
  questionNum: { background: 'var(--forest-500)', color: 'white', borderRadius: '50%', width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 },
  questionText: { flex: 1, fontSize: 14, color: 'var(--slate-800)', lineHeight: 1.5 },
  deleteBtn: { background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, flexShrink: 0 },
  optionsList: { display: 'flex', flexDirection: 'column', gap: 6, marginLeft: 36 },
  optionItem: { fontSize: 13, color: 'var(--slate-600)', padding: '6px 10px', borderRadius: 6, background: 'white', border: '1px solid var(--color-border)' },
  optionCorrect: { background: '#dcfce7', borderColor: '#86efac', color: '#166534', fontWeight: 600 },
  explanation: { fontSize: 12, color: 'var(--forest-600)', marginTop: 10, marginLeft: 36, fontStyle: 'italic' },
};